import { DEFAULT_EVALUATIONS } from '@sstk/switchr-core';
import { StatsigServer } from 'statsig-node';
import { DynamoDBClient } from '@aws-sdk/client-dynamodb';
import { PutCommand, GetCommand, DynamoDBDocumentClient } from '@aws-sdk/lib-dynamodb';

/** Returns the value of the Experiment value if it exists in the evaluations
 * otherwise returns the default value.
 * It will not record impression for the experiment.
 * For getting experiment & recording an impressions (exposure event) server side use Switchr.getExperiment
 */
var getExperimentValue = function getExperimentValue(evaluations, id, defaultValue) {
  var _evaluations$experime;
  var value = (_evaluations$experime = evaluations.experiments) == null || (_evaluations$experime = _evaluations$experime[id]) == null ? void 0 : _evaluations$experime.value;
  if (value === undefined) {
    return defaultValue;
  }
  return value;
};
/** Returns the value of the DynamicConfig if it exists in the evaluations
 * otherwise returns the default value
 * Note using this will not send exposure events to Statsig.
 */
var getDynamicConfigValue = function getDynamicConfigValue(evaluations, id, defaultValue) {
  var _evaluations$dynamicC;
  var value = (_evaluations$dynamicC = evaluations.dynamicConfigs) == null || (_evaluations$dynamicC = _evaluations$dynamicC[id]) == null ? void 0 : _evaluations$dynamicC.value;
  if (value === undefined) {
    return defaultValue;
  }
  return value;
};
/** Returns the value of the gate if it exists in the evaluations
 * otherwise returns false.
 * Note using this will not send exposure events to Statsig.
 */
var getGateValue = function getGateValue(evaluations, id) {
  var _evaluations$gates;
  var value = (_evaluations$gates = evaluations.gates) == null || (_evaluations$gates = _evaluations$gates[id]) == null ? void 0 : _evaluations$gates.value;
  if (value === undefined) {
    return false;
  }
  return value;
};

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}

// DynamoDB Table Name
// dev: arn:aws:dynamodb:us-east-1:825727067561:table/StatsigClientDataAdapter

function _catch(body, recover) {
  try {
    var result = body();
  } catch (e) {
    return recover(e);
  }
  if (result && result.then) {
    return result.then(void 0, recover);
  }
  return result;
}
var TABLE_NAME = 'StatsigClientDataAdapter';
var DynamoDbDataAdapter = /*#__PURE__*/function () {
  function DynamoDbDataAdapter(config) {
    var _this = this,
      _this2 = this;
    // Wrap DynamoDBClient with DynamoDBDocumentClient for simplified operations
    this.ddbDocClient = void 0;
    // Write Data to DynamoDB
    this.set = function (key, value) {
      try {
        var _temp = _catch(function () {
          var item = {
            statsigKey: key,
            // Partition Key
            value: value
          };
          var command = new PutCommand({
            TableName: TABLE_NAME,
            Item: item
          });
          return Promise.resolve(_this.ddbDocClient.send(command)).then(function () {});
        }, function (error) {
          console.error('Error writing to DynamoDB:', error);
        });
        return Promise.resolve(_temp && _temp.then ? _temp.then(function () {}) : void 0);
      } catch (e) {
        return Promise.reject(e);
      }
    };
    // Read Data from DynamoDB
    this.get = function (key) {
      try {
        return Promise.resolve(_catch(function () {
          var dynamoDbKey = {
            statsigKey: key // Partition Key
          };
          var command = new GetCommand({
            TableName: TABLE_NAME,
            Key: dynamoDbKey
          });
          return Promise.resolve(_this2.ddbDocClient.send(command)).then(function (response) {
            if (response.Item) {
              return {
                result: "" + response.Item
              };
            } else {
              return {
                error: new Error("No item found with the given key: " + key)
              };
            }
          });
        }, function (error) {
          return {
            error: new Error("Error reading from DynamoDB: " + error)
          };
        }));
      } catch (e) {
        return Promise.reject(e);
      }
    };
    var region = config.region,
      accessKeyId = config.accessKeyId,
      secretAccessKey = config.secretAccessKey,
      sessionToken = config.sessionToken;
    var ddbClient;
    if (secretAccessKey && accessKeyId) {
      ddbClient = new DynamoDBClient({
        region: region,
        credentials: _extends({
          accessKeyId: accessKeyId,
          secretAccessKey: secretAccessKey
        }, sessionToken ? {
          sessionToken: sessionToken
        } : {})
      });
    } else {
      ddbClient = new DynamoDBClient({
        region: region
      });
    }
    this.ddbDocClient = DynamoDBDocumentClient.from(ddbClient);
  }
  var _proto = DynamoDbDataAdapter.prototype;
  _proto.initialize = function initialize() {
    return Promise.resolve();
  };
  _proto.shutdown = function shutdown() {
    return Promise.resolve();
  };
  return DynamoDbDataAdapter;
}();

var IS_EXPERIMENT_ACTIVE = 'is_experiment_active';
var DISABLED = 'disabled';
var createStatsigUser = function createStatsigUser(_ref) {
  var _ref$id = _ref.id,
    id = _ref$id === void 0 ? '' : _ref$id,
    customIds = _ref.customIds,
    _ref$attributes = _ref.attributes,
    attributes = _ref$attributes === void 0 ? {} : _ref$attributes,
    custom = _ref.custom;
  return _extends({}, customIds ? {
    customIDs: customIds
  } : {
    userID: id
  }, attributes, {
    custom: custom
  });
};
var createConfig = function createConfig(_ref2) {
  var rule_id = _ref2.rule_id,
    value = _ref2.value;
  return {
    groupId: rule_id,
    value: value
  };
};
var createGate = function createGate(_ref3) {
  var rule_id = _ref3.rule_id,
    value = _ref3.value;
  return {
    groupId: rule_id,
    value: value
  };
};
var parseConfigs = function parseConfigs(statsigConfigs) {
  return Object.values(statsigConfigs).reduce(function (acc, details) {
    // todo: confirm if this is the best way to detect if a config is an experiment
    var isExperiment = Object.hasOwn(details, IS_EXPERIMENT_ACTIVE);
    if (details.rule_id !== DISABLED) {
      if (isExperiment) {
        if (
        // if user is in the experiment or a part of an override
        details.is_user_in_experiment || details.rule_id.endsWith('override')) {
          var _extends2;
          acc.experiments = _extends({}, acc.experiments, (_extends2 = {}, _extends2[details.name] = createConfig(details), _extends2));
        }
      } else {
        var _extends3;
        acc.dynamicConfigs = _extends({}, acc.dynamicConfigs, (_extends3 = {}, _extends3[details.name] = createConfig(details), _extends3));
      }
    }
    return acc;
  }, {
    dynamicConfigs: {},
    experiments: {}
  });
};
var parseGates = function parseGates(statsigGates) {
  return Object.values(statsigGates).reduce(function (acc, details) {
    var _extends4;
    acc.gates = _extends({}, acc.gates, (_extends4 = {}, _extends4[details.name] = createGate(details), _extends4));
    return acc;
  }, {
    gates: {}
  });
};

/**
 * The SSTK specific interface for connecting with the Statsig Server SDK
 */
var ServerClient = /*#__PURE__*/function () {
  function ServerClient(key, options) {
    this.statsig = void 0;
    var env = options.env,
      localMode = options.localMode,
      useDataAdapter = options.useDataAdapter,
      customDataAdapter = options.customDataAdapter,
      dynamoDbConfig = options.dynamoDbConfig,
      rulesUpdatedCallback = options.rulesUpdatedCallback,
      statsigOptions = options.statsigOptions;
    if (useDataAdapter && customDataAdapter && dynamoDbConfig === undefined) {
      throw new Error("dynamoDbConfig must be defined when useDataAdapter without custom data adapter");
    }
    this.statsig = new StatsigServer(key, _extends({
      environment: {
        tier: env
      },
      localMode: localMode,
      rulesUpdatedCallback: rulesUpdatedCallback
    }, useDataAdapter ? {
      dataAdapter: customDataAdapter || new DynamoDbDataAdapter(dynamoDbConfig)
    } : {}, statsigOptions));
  }
  var _proto = ServerClient.prototype;
  _proto.getVendorClient = function getVendorClient() {
    if (!this.statsig) {
      throw new Error('Statsig client not initialized');
    }
    return this.statsig;
  };
  _proto.initialize = function initialize() {
    return this.statsig.initializeAsync();
  };
  _proto.shutdown = function shutdown() {
    // todo: flush /shutdownAsync
    return this.statsig.shutdown();
  };
  _proto.checkGate = function checkGate(id, user) {
    return this.statsig.checkGate(createStatsigUser(user), id);
  };
  _proto.getExperiment = function getExperiment(id, user) {
    return this.statsig.getExperiment(createStatsigUser(user), id);
  }
  /**
   * Returns all of the evaluations of the configs, experiemnts and gates for a user.
   * Primarily used to bootstrap the browser sdk
   * @param user Switchr User
   * @returns Switchr Evaluations
   */;
  _proto.getAllEvaluations = function getAllEvaluations(user) {
    var data = this.statsig.getClientInitializeResponse(createStatsigUser(user), undefined, {
      hash: 'none'
    });
    if (!data) {
      throw new Error('Statsig: failed to retrieve initialized data');
    }
    return _extends({}, parseConfigs(data.dynamic_configs), parseGates(data.feature_gates));
  };
  return ServerClient;
}();

/**
 * The main class for interacting with configs, experiments and gates.
 */
var Switchr = /*#__PURE__*/function () {
  function Switchr(key, options) {
    this.client = void 0;
    this.errorHandler = void 0;
    this.client = new ServerClient(key, _extends({}, options, {
      env: options.env || ''
    }));
    this.errorHandler = options.onError;
  }
  var _proto = Switchr.prototype;
  _proto.getClient = function getClient() {
    if (!this.client) {
      throw new Error('Switchr client not initialized');
    }
    return this.client;
  };
  _proto.getVendorClient = function getVendorClient() {
    if (!this.client) {
      throw new Error('Vendor client not initialized');
    }
    return this.getClient().getVendorClient();
  }
  /**
   * Helper function that invokes the error handler callback if one was provided
   * @param err
   */;
  _proto._errorHandler = function _errorHandler(err) {
    if (this.errorHandler) {
      this.errorHandler(err);
    }
  }
  /**
   *
   * @param task Helper function for catching any errors that occur and reporting them to the error handler
   * @param defaultValue
   * @returns
   */;
  _proto._swallowError = function _swallowError(task, defaultValue) {
    try {
      var resp = task();
      return resp;
    } catch (err) {
      this._errorHandler(err);
      return defaultValue;
    }
  }
  /**
   *
   * @returns Initializes the SDK via an asynchronous request so it can perform evaluations
   */;
  _proto.initialize = function initialize() {
    var _this = this;
    return this.client.initialize()["catch"](function (err) {
      _this._errorHandler(err);
      return null;
    });
  }
  /**
   *
   * @returns Shuts down the SDK
   */;
  _proto.shutdown = function shutdown() {
    var _this2 = this;
    return this._swallowError(function () {
      return _this2.client.shutdown();
    }, undefined);
  }
  /**
   * Returns the boolean value for the specified gate and user.
   * This will not send exposure events
   */;
  _proto.checkGate = function checkGate(id, user) {
    var _this3 = this;
    return this._swallowError(function () {
      return _this3.client.checkGate(id, user);
    }, false);
  }
  /**
   * Returns the boolean value for the specified gate and user & records an impression for the user to the statsig platform.
   */;
  _proto.getExperiment = function getExperiment(id, user) {
    var _this4 = this;
    return this._swallowError(function () {
      return _this4.client.getExperiment(id, user);
    }, {});
  }
  /**
   * Returns all of the evaluations of the configs, experiemnts and gates for a user.
   * Primarily used to bootstrap the browser sdk.  Getting all evaluations does not trigger an impression for a user.
   */;
  _proto.getAllEvaluations = function getAllEvaluations(user) {
    var _this5 = this;
    return this._swallowError(function () {
      return _this5.client.getAllEvaluations(user);
    }, DEFAULT_EVALUATIONS);
  };
  return Switchr;
}();

export { Switchr, getDynamicConfigValue, getExperimentValue, getGateValue };
//# sourceMappingURL=index.js.map
